/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { requestBaseURL, requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { getAxios } from '@/lib/axios';
import { isAxiosError, type AxiosInstance } from 'axios';
import { type QueryClient } from '@tanstack/react-query';
import { createQuery, type CustomUseQueryOptions } from '@/utils/createReactQuery';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useRouter } from 'next/navigation';
import { type PageTypeResponseModel } from '@/api/types/browse-search-types';
import { parseUrl } from '@/utils/urlHelpers';
import { showXMPreviewDate } from '@/utils/showXMPreviewDate';
import { sanitizeUrlWithParamList } from '../../../utils/urlHelpers';
import { acceptableShelfQueryParams } from '../../../constants/queryParams';
import { useLocale } from '@/hooks/useLocale';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import type { Country, PageTypeData, UrlWithQuery } from '@/types';
import { routePaths } from '@/constants/routePaths';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { useQueryResultOnSuccess } from '@/hooks/query/useQueryResultOnSuccess';
import { useQueryResultOnError } from '@/hooks/query/useQueryResultOnError';
import { useExperimentId } from '@/utils/searchExperiment/useExperimentId';
import environmentConfig from '@/config/environment';

type QueryOptions = {
  canonicalPath: string | undefined;
  preview: boolean;
  country: string;
  gcpUrlEnabled: boolean;
  queryParams?: string;
  experimentId: string | undefined;
};
type PageTypeError = {
  altBlockScript: string;
  appId: string;
  blockScript: string;
  customLogo: null;
  firstPartyEnabled: boolean;
  hostUrl: string;
  isMobile: string;
  jsClientSrc: string;
  uuid: string;
  vid: string;
};

type PageTypeResponseData = PageTypeResponseModel;

const EXTERNAL_SITES = ['/expertips', '/company', '/diy', '/locations'];

const URL = `${requestBaseURL}/external/product-discovery/browse-search/v2/page-types`;

const GCP_URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-seo-data-bs/v2/page-types`;

const ES_URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-seo-data-bs/v3/page-types`;

const buildFacetQueryParams = (
  parsedUrl: UrlWithQuery,
  acesEnabled: boolean,
): string | undefined => {
  if (!acesEnabled || !parsedUrl.query.facet) {
    return undefined;
  }
  return `facet=${parsedUrl.query.facet}`;
};

const getPageType = async (options: QueryOptions, axiosInstance?: AxiosInstance) => {
  const { canonicalPath, preview, country, gcpUrlEnabled, queryParams, experimentId } = options;
  const isES = (experimentId === 'vertex' || experimentId === 'elasticsearch') && gcpUrlEnabled;
  const response = await getAxios(axiosInstance).get<PageTypeResponseData>(
    isES ? ES_URL : gcpUrlEnabled ? GCP_URL : URL,
    {
      params: {
        canonicalPath,
        preview,
        country,
        ...(gcpUrlEnabled && queryParams && { queryParams }),
      },
      ...(gcpUrlEnabled && { withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS }),
    },
  );
  return selector(response.data, gcpUrlEnabled);
};

const {
  query: pageTypeQuery,
  useData: usePageTypeAPI,
  prefetch: prefetchPageTypeAPI,
} = createQuery<PageTypeData, QueryOptions>('pageType', getPageType);

const selector = (
  { redirectUrl, acesPageTypeResult, pageTypeResult }: PageTypeResponseData,
  gcpUrlEnabled: boolean,
): PageTypeData =>
  ({
    redirectLocation: redirectUrl,
    ...acesPageTypeResult,
    ...pageTypeResult,
    catalogRepositoryId: gcpUrlEnabled
      ? acesPageTypeResult?.catalogId
      : pageTypeResult?.catalogRepositoryId,
    catalogRepositoryName: gcpUrlEnabled
      ? acesPageTypeResult?.catalogName
      : pageTypeResult?.catalogRepositoryName,
    categoryRepositoryIdPath: gcpUrlEnabled
      ? acesPageTypeResult?.catalogIdPath
      : pageTypeResult?.categoryRepositoryIdPath,
  }) as PageTypeData;

export const usePageType = ({
  canonicalPath,
  enabled = true,
  placeholderData,
}: {
  enabled?: boolean;
  canonicalPath?: string;
} & Omit<CustomUseQueryOptions<PageTypeData, PageTypeData>, 'select'> = {}) => {
  const legacyRouter = useLegacyRouter();
  const router = useRouter();
  const locale = useLocale();
  const { acesEnabled } = useAcesFlags();
  const experimentId = useExperimentId();
  const country = getCountryFromLocale(locale);
  const parsedUrl = parseUrl(canonicalPath || legacyRouter.asPath);
  const newCanonicalPath = parsedUrl.url;
  const isExternalSite = EXTERNAL_SITES.some((route) => newCanonicalPath.includes(route));
  const queryParamsString = buildFacetQueryParams(parsedUrl, acesEnabled);

  const queryResult = usePageTypeAPI({
    enabled:
      legacyRouter.route !== '/errorPage' &&
      legacyRouter.route === '/[...seoUrlPath]' &&
      !newCanonicalPath.includes('.') &&
      !isExternalSite &&
      !!enabled,
    placeholderData,
    canonicalPath: newCanonicalPath,
    staleTime: Infinity,
    preview: showXMPreviewDate(),
    country,
    gcpUrlEnabled: acesEnabled,
    experimentId,
    ...(queryParamsString && { queryParams: queryParamsString }),
  });

  useQueryResultOnSuccess(
    queryResult,
    (data) => {
      if (!data?.redirectLocation) {
        return;
      }

      const { intcmp } = legacyRouter.query;
      const { url, query: queryParams } = parseUrl(data?.redirectLocation);
      const sanitizedRedirectPath = sanitizeUrlWithParamList(
        {
          ...queryParams,
          intcmp,
        },
        acceptableShelfQueryParams,
        url,
      );

      router.replace(sanitizedRedirectPath);
    },
    true,
  );

  useQueryResultOnError(queryResult, (error) => {
    if (
      isAxiosError<PageTypeError>(error) &&
      error?.response?.status === 403 &&
      !!error?.response?.data?.appId
    ) {
      return;
    }
    try {
      // eslint-disable-next-line no-console
      console.log(`An error occurred while requesting page type: `, error);
      router.replace(routePaths.errorPage);
      return;
    } catch {
      throw new Error(`There was a problem redirecting to ${routePaths.errorPage}`);
    }
  });

  return queryResult;
};

export const getPageTypeFromCache = (
  queryClient: QueryClient,
  {
    canonicalPath,
    country,
    gcpUrlEnabled,
    experimentId,
  }: {
    canonicalPath: string;
    country: Country;
    gcpUrlEnabled: boolean;
    experimentId: string | undefined;
  },
) => {
  const parsedUrl = parseUrl(canonicalPath);
  const queryParamsString = buildFacetQueryParams(parsedUrl, gcpUrlEnabled);

  const options: QueryOptions = {
    canonicalPath: parsedUrl.url,
    preview: showXMPreviewDate(),
    country,
    gcpUrlEnabled,
    ...(queryParamsString && { queryParams: queryParamsString }),
    experimentId,
  };
  return queryClient.getQueryData<PageTypeData>(pageTypeQuery.getFullKey(options));
};

export const prefetchPageType = async (
  queryClient: QueryClient,
  axiosInstance: AxiosInstance,
  {
    canonicalPath,
    country,
    gcpUrlEnabled,
    experimentId,
  }: {
    canonicalPath: string;
    country: Country;
    gcpUrlEnabled: boolean;
    experimentId: string | undefined;
  },
) => {
  const parsedUrl = parseUrl(canonicalPath);
  const queryParamsString = buildFacetQueryParams(parsedUrl, gcpUrlEnabled);

  const options: QueryOptions = {
    canonicalPath: parsedUrl.url,
    preview: showXMPreviewDate(),
    country,
    gcpUrlEnabled,
    ...(queryParamsString && { queryParams: queryParamsString }),
    experimentId,
  };

  return prefetchPageTypeAPI(queryClient, options, axiosInstance);
};
